import {
	AuditAction,
	AuditEntityType,
	type AuditLog,
} from "@pilatestime/shared-types";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ClipboardList, Inbox, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	fetchAuditLogByIdQueryOptions,
	fetchAuditLogsQueryOptions,
} from "@/api/auditLogs";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Pagination } from "@/components/ui/pagination";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useApiParameters } from "@/hooks/useApiParameters";
import { OrganizationSettingsNavigation } from "@/routes/_auth/app/_app/org/$orgId/settings/-components/OrganizationSettingsNavigation";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/settings/audit-events/",
)({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		return { userId, token };
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();
	const { userId, token } = Route.useLoaderData();
	const { orgId } = Route.useParams();
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(20);
	const [entityTypeFilter, setEntityTypeFilter] = useState<string>("all");
	const [actionFilter, setActionFilter] = useState<string>("all");
	const [selectedLogId, setSelectedLogId] = useState<string | null>(null);

	const { data } = useSuspenseQuery(
		fetchAuditLogsQueryOptions(userId, token, orgId, {
			page,
			limit,
			entityType:
				entityTypeFilter !== "all"
					? (entityTypeFilter as AuditEntityType)
					: undefined,
			action: actionFilter !== "all" ? actionFilter : undefined,
		}),
	);

	// Lazy load details only when a log is selected
	const { data: selectedLogDetails, isLoading: isLoadingDetails } = useQuery({
		...fetchAuditLogByIdQueryOptions(userId, token, orgId, selectedLogId ?? ""),
		enabled: !!selectedLogId,
	});

	const formatDate = (date: Date | string) => {
		const d = typeof date === "string" ? new Date(date) : date;
		return d.toLocaleString();
	};

	const getActionBadgeVariant = (action: string) => {
		switch (action) {
			case AuditAction.CREATED:
			case AuditAction.BULK_CREATED:
				return "default";
			case AuditAction.UPDATED:
			case AuditAction.BULK_UPDATED:
			case AuditAction.STATUS_CHANGED:
				return "secondary";
			case AuditAction.DELETED:
			case AuditAction.BULK_DELETED:
			case AuditAction.CANCELED:
				return "destructive";
			case AuditAction.RESTORED:
			case AuditAction.REACTIVATED:
				return "outline";
			default:
				return "secondary";
		}
	};

	const paginationMeta = {
		page: data.page,
		limit: data.pageSize,
		total: data.total,
		totalPages: Math.ceil(data.total / data.pageSize),
		hasNextPage: data.page * data.pageSize < data.total,
		hasPreviousPage: data.page > 1,
	};

	const handleRowClick = (logId: string) => {
		setSelectedLogId(logId);
	};

	const handleCloseDialog = () => {
		setSelectedLogId(null);
	};

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("orgSettings.title")}
				description={t("orgSettings.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
				hideOnMobile={false}
			/>
			<div
				className="flex-1 flex h-[calc(100vh-4rem)] min-h-0 overflow-hidden"
				data-testid="organization-settings-container"
			>
				<OrganizationSettingsNavigation activeTab="audit-events" />
				<div
					className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
					data-testid="organization-settings-main-content-area"
				>
					<Card className="h-full flex flex-col">
						<CardHeader className="px-3 md:px-6 pt-3 md:pt-6 pb-2 md:pb-4">
							<CardTitle className="flex items-center space-x-2 text-sm md:text-base">
								<ClipboardList className="h-4 w-4 md:h-5 md:w-5 text-primary" />
								<span>{t("orgSettings.auditEvents.title")}</span>
							</CardTitle>
							<CardDescription className="text-xs md:text-sm">
								{t("orgSettings.auditEvents.description")}
							</CardDescription>
						</CardHeader>
						<CardContent className="flex-1 flex flex-col space-y-4 px-3 md:px-6 pb-3 md:pb-6 overflow-hidden">
							{/* Filters */}
							<div className="flex flex-wrap gap-3">
								<Select
									value={entityTypeFilter}
									onValueChange={setEntityTypeFilter}
								>
									<SelectTrigger className="w-[180px]">
										<SelectValue
											placeholder={t(
												"orgSettings.auditEvents.filters.entityType",
											)}
										/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">
											{t("orgSettings.auditEvents.filters.allEntityTypes")}
										</SelectItem>
										{Object.values(AuditEntityType).map((type) => (
											<SelectItem key={type} value={type}>
												{t(`orgSettings.auditEvents.entityTypes.${type}`)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Select value={actionFilter} onValueChange={setActionFilter}>
									<SelectTrigger className="w-[180px]">
										<SelectValue
											placeholder={t("orgSettings.auditEvents.filters.action")}
										/>
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">
											{t("orgSettings.auditEvents.filters.allActions")}
										</SelectItem>
										{Object.values(AuditAction).map((action) => (
											<SelectItem key={action} value={action}>
												{t(`orgSettings.auditEvents.actions.${action}`)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							{/* Pagination */}
							<Pagination
								pagination={paginationMeta}
								onPageChange={setPage}
								onPageSizeChange={(newLimit) => {
									setLimit(newLimit);
									setPage(1);
								}}
							/>
							{/* Table */}
							<div className="flex-1 overflow-auto">
								{data.items.length === 0 ? (
									<div className="flex flex-col items-center justify-center h-full text-center py-12">
										<Inbox className="h-12 w-12 text-base-content/30 mb-4" />
										<h3 className="text-lg font-medium">
											{t("orgSettings.auditEvents.noEvents")}
										</h3>
										<p className="text-sm text-base-content/60">
											{t("orgSettings.auditEvents.noEventsDescription")}
										</p>
									</div>
								) : (
									<table className="table table-pin-rows table-zebra w-full">
										<thead>
											<tr>
												<th className="bg-base-200">
													{t("orgSettings.auditEvents.columns.timestamp")}
												</th>
												<th className="bg-base-200">
													{t("orgSettings.auditEvents.columns.entityType")}
												</th>
												<th className="bg-base-200">
													{t("orgSettings.auditEvents.columns.action")}
												</th>
												<th className="bg-base-200 hidden md:table-cell">
													{t("orgSettings.auditEvents.columns.user")}
												</th>
												<th className="bg-base-200 w-20" />
											</tr>
										</thead>
										<tbody>
											{data.items.map((log: AuditLog) => (
												<tr key={log.id} className="hover:bg-base-300">
													<td className="whitespace-nowrap">
														{formatDate(log.createdAt)}
													</td>
													<td>
														{t(
															`orgSettings.auditEvents.entityTypes.${log.entityType}`,
														)}
													</td>
													<td>
														<Badge variant={getActionBadgeVariant(log.action)}>
															{t(
																`orgSettings.auditEvents.actions.${log.action}`,
															)}
														</Badge>
													</td>
													<td className="hidden md:table-cell">
														{log.user?.email ??
															t("orgSettings.auditEvents.systemUser")}
													</td>
													<td>
														<Button
															variant="ghost"
															size="sm"
															onClick={() => handleRowClick(log.id)}
														>
															{t("orgSettings.auditEvents.viewDetails")}
														</Button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								)}
							</div>
						</CardContent>
					</Card>
				</div>
			</div>

			{/* Details Dialog */}
			<Dialog open={!!selectedLogId} onOpenChange={handleCloseDialog}>
				<DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>
							{t("orgSettings.auditEvents.detailsDialog.title")}
						</DialogTitle>
					</DialogHeader>
					{isLoadingDetails ? (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="h-8 w-8 animate-spin text-primary" />
							<span className="ml-2">
								{t("orgSettings.auditEvents.detailsDialog.loading")}
							</span>
						</div>
					) : selectedLogDetails ? (
						<div className="space-y-4">
							{/* Basic Info */}
							<div className="grid grid-cols-2 gap-4">
								<div>
									<span className="text-sm font-medium text-base-content/60">
										{t("orgSettings.auditEvents.columns.timestamp")}
									</span>
									<p>{formatDate(selectedLogDetails.createdAt)}</p>
								</div>
								<div>
									<span className="text-sm font-medium text-base-content/60">
										{t("orgSettings.auditEvents.columns.entityType")}
									</span>
									<p>
										{t(
											`orgSettings.auditEvents.entityTypes.${selectedLogDetails.entityType}`,
										)}
									</p>
								</div>
								<div>
									<span className="text-sm font-medium text-base-content/60">
										{t("orgSettings.auditEvents.columns.action")}
									</span>
									<p>
										<Badge
											variant={getActionBadgeVariant(selectedLogDetails.action)}
										>
											{t(
												`orgSettings.auditEvents.actions.${selectedLogDetails.action}`,
											)}
										</Badge>
									</p>
								</div>
								<div>
									<span className="text-sm font-medium text-base-content/60">
										{t("orgSettings.auditEvents.columns.user")}
									</span>
									<p>
										{selectedLogDetails.user?.email ??
											t("orgSettings.auditEvents.systemUser")}
									</p>
								</div>
								<div className="col-span-2">
									<span className="text-sm font-medium text-base-content/60">
										{t("orgSettings.auditEvents.detailsDialog.entityId")}
									</span>
									<p className="font-mono text-sm break-all">
										{selectedLogDetails.entityId}
									</p>
								</div>
								{selectedLogDetails.reason && (
									<div className="col-span-2">
										<span className="text-sm font-medium text-base-content/60">
											{t("orgSettings.auditEvents.detailsDialog.reason")}
										</span>
										<p>{selectedLogDetails.reason}</p>
									</div>
								)}
							</div>

							{/* Changes */}
							{selectedLogDetails.changes &&
								Object.keys(selectedLogDetails.changes).length > 0 && (
									<div>
										<h4 className="text-sm font-medium text-base-content/60 mb-2">
											{t(
												"orgSettings.auditEvents.detailsDialog.changesSection",
											)}
										</h4>
										<div className="bg-base-200 rounded-lg p-3 space-y-2">
											{Object.entries(selectedLogDetails.changes).map(
												([field, change]) => (
													<div
														key={field}
														className="grid grid-cols-3 gap-2 text-sm"
													>
														<span className="font-medium">{field}</span>
														<span className="text-error line-through">
															{JSON.stringify(change.old)}
														</span>
														<span className="text-success">
															{JSON.stringify(change.new)}
														</span>
													</div>
												),
											)}
										</div>
									</div>
								)}

							{/* Before/After State */}
							{(selectedLogDetails.beforeState ||
								selectedLogDetails.afterState) && (
								<div className="grid grid-cols-2 gap-4">
									{selectedLogDetails.beforeState && (
										<div>
											<h4 className="text-sm font-medium text-base-content/60 mb-2">
												{t("orgSettings.auditEvents.detailsDialog.beforeState")}
											</h4>
											<pre className="bg-base-200 rounded-lg p-3 text-xs overflow-auto max-h-48">
												{JSON.stringify(
													selectedLogDetails.beforeState,
													null,
													2,
												)}
											</pre>
										</div>
									)}
									{selectedLogDetails.afterState && (
										<div>
											<h4 className="text-sm font-medium text-base-content/60 mb-2">
												{t("orgSettings.auditEvents.detailsDialog.afterState")}
											</h4>
											<pre className="bg-base-200 rounded-lg p-3 text-xs overflow-auto max-h-48">
												{JSON.stringify(selectedLogDetails.afterState, null, 2)}
											</pre>
										</div>
									)}
								</div>
							)}

							<div className="flex justify-end pt-4">
								<Button variant="outline" onClick={handleCloseDialog}>
									{t("orgSettings.auditEvents.detailsDialog.close")}
								</Button>
							</div>
						</div>
					) : null}
				</DialogContent>
			</Dialog>
		</div>
	);
}
